import React, { useState } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';

import i18n from '@/utils/i18n';

import { useGetEventAccountQuery } from '../../../../graphql/queries/getEventAccount.generated';
import { useDeleteItemLocationMutation } from '../../../../graphql/mutations/deleteItemLocation.generated';
import { useDeleteAuthorizedSignerMutation } from '../../../../graphql/mutations/deleteAuthorizedSigner.generated';
import { useCreateAuthorizedSignerMutation } from '../../../../graphql/mutations/createAuthorizedSigner.generated';
import { useCreateLocationForEventAccountMutation } from '../../../../graphql/queries/createLocationForEventAccount.generated';
import ClassicLoadingSpinner from '../../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { FaTrash } from 'react-icons/fa';

type ItemLocationReducedType = {
  id: string;
  name: string;
};

type AuthorizedSignerReducedType = {
  id: string;
  name: string;
};

export const EventAccountSettings = (props: { eventAccountId: string }) => {
  const { eventAccountId } = props;

  const [isAddItemModalOpen, setIsAddItemModalOpen] = React.useState(false);
  const [addItemModalTitle, setAddItemModalTitle] = React.useState('');
  const [addModalAction, setAddModalAction] = React.useState<
    'itemLocation' | 'authorizedSigner'
  >('itemLocation');
  const [name, setName] = React.useState('');

  const [showItemLocationRemoveModal, setShowItemLocationRemoveModal] =
    useState(false);
  const [itemLocationToRemove, setItemLocationToRemove] =
    useState<ItemLocationReducedType>();
  const [showAuthorizesSignerRemoveModal, setShowAuthorizesSignerRemoveModal] =
    useState(false);
  const [authorizedSignerToRemove, setAuthorizedSignerToRemove] =
    useState<AuthorizedSignerReducedType>();

  const { data, error, loading, refetch } = useGetEventAccountQuery({
    errorPolicy: 'all',
    variables: {
      id: eventAccountId,
    },
  });

  const [deleteItemLocation] = useDeleteItemLocationMutation({
    onCompleted: () => refetch(),
  });

  const [deleteAuthorizedSigner] = useDeleteAuthorizedSignerMutation({
    onCompleted: () => refetch(),
  });

  const [createItemLocation] = useCreateLocationForEventAccountMutation({
    onCompleted: () => refetch(),
  });

  const [createAuthorizedSigner] = useCreateAuthorizedSignerMutation({
    onCompleted: () => refetch(),
  });

  if ((data == null && !loading) || error != null) {
    return <div>{i18n.t('EventAccountSettings:errorOnLoad')}</div>;
  }
  if (loading) {
    return <ClassicLoadingSpinner />;
  }

  function addItemModal() {
    return (
      <Modal
        isOpen={isAddItemModalOpen}
        toggle={() => setIsAddItemModalOpen(false)}
      >
        <ModalHeader>{addItemModalTitle}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="name">{i18n.t('Common:name')}</Label>
            <Input
              type="text"
              name="username"
              id="username"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              if (addModalAction === 'itemLocation') {
                createItemLocation({
                  variables: {
                    event_account_id: eventAccountId,
                    name: name,
                  },
                });
              } else {
                createAuthorizedSigner({
                  variables: {
                    event_account_id: eventAccountId,
                    name,
                  },
                });
              }

              setIsAddItemModalOpen(false);
            }}
          >
            {addModalAction === 'itemLocation' &&
              i18n.t('EventAccountSettings:addItemLocation')}
            {addModalAction === 'authorizedSigner' &&
              i18n.t('EventAccountSettings:addAuthorizedSigner')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => setIsAddItemModalOpen(false)}
          >
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  const itemLocations = data?.eventAccount?.item_locations || [];
  const authorizedSigners = data?.eventAccount?.authorized_signers || [];
  return (
    <div>
      <h4>{i18n.t('EventAccountSettings:itemLocations')}</h4>
      <Button
        className="float-end"
        onClick={() => {
          setIsAddItemModalOpen(true);
          setAddItemModalTitle(i18n.t('EventAccountSettings:addItemLocation'));
          setAddModalAction('itemLocation');
        }}
      >
        {i18n.t('EventAccountSettings:addItemLocation')}
      </Button>
      <Table>
        <thead>
          <tr>
            <th>{i18n.t('Common:name')}</th>
            <th>{i18n.t('Common:delete')}</th>
          </tr>
        </thead>
        <tbody>
          {itemLocations.map((itemLocation) => (
            <tr key={itemLocation.id}>
              <td>{itemLocation.name}</td>
              <td>
                <Button
                  color="danger"
                  onClick={() => {
                    setItemLocationToRemove(itemLocation);
                    setShowItemLocationRemoveModal(true);
                  }}
                >
                  <FaTrash /> {i18n.t('Common:delete')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal
        isOpen={showItemLocationRemoveModal}
        toggle={() => {
          setShowItemLocationRemoveModal(false);
        }}
      >
        <ModalHeader
          toggle={() => {
            setShowItemLocationRemoveModal(false);
          }}
        >
          {i18n.t('Common:modalTitle')}
        </ModalHeader>
        <ModalBody>
          {i18n.t('Common:modalBody')}: <p></p>
          {i18n.t('Common:id')}: <p>{itemLocationToRemove?.id}</p>
          {i18n.t('Common:name')}: <p>{itemLocationToRemove?.name}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              if (itemLocationToRemove?.id) {
                deleteItemLocation({
                  variables: { id: itemLocationToRemove.id },
                });
              }

              setShowItemLocationRemoveModal(false);
            }}
          >
            {i18n.t('Common:yes')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => {
              setShowItemLocationRemoveModal(false);
            }}
          >
            {i18n.t('Common:no')}
          </Button>
        </ModalFooter>
      </Modal>
      <h4>{i18n.t('EventAccountSettings:authorizedSigners')}</h4>
      <Button
        className="float-end"
        onClick={() => {
          setIsAddItemModalOpen(true);
          setAddItemModalTitle(
            i18n.t('EventAccountSettings:addAuthorizedSigner'),
          );
          setAddModalAction('authorizedSigner');
        }}
      >
        {i18n.t('EventAccountSettings:addAuthorizedSigner')}
      </Button>
      <Table>
        <thead>
          <tr>
            <th>{i18n.t('Common:name')}</th>
            <th>{i18n.t('Common:delete')}</th>
          </tr>
        </thead>
        <tbody>
          {authorizedSigners.map((authorizedSigner) => (
            <tr key={authorizedSigner.id}>
              <td>{authorizedSigner.name}</td>
              <td>
                <Button
                  color="danger"
                  onClick={() => {
                    setAuthorizedSignerToRemove(authorizedSigner);
                    setShowAuthorizesSignerRemoveModal(true);
                  }}
                >
                  <FaTrash /> {i18n.t('Common:delete')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal
        isOpen={showAuthorizesSignerRemoveModal}
        toggle={() => {
          setShowAuthorizesSignerRemoveModal(false);
        }}
      >
        <ModalHeader
          toggle={() => {
            setShowAuthorizesSignerRemoveModal(false);
          }}
        >
          {i18n.t('Common:modalTitle')}
        </ModalHeader>
        <ModalBody>
          {i18n.t('Common:modalBody')}: <p></p>
          {i18n.t('Common:id')}: <p>{authorizedSignerToRemove?.id}</p>
          {i18n.t('Common:name')}: <p>{authorizedSignerToRemove?.name}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              if (authorizedSignerToRemove?.id) {
                deleteAuthorizedSigner({
                  variables: {
                    authorized_signer_id: authorizedSignerToRemove.id,
                  },
                });
              }

              setShowAuthorizesSignerRemoveModal(false);
            }}
          >
            {i18n.t('Common:yes')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => {
              setShowAuthorizesSignerRemoveModal(false);
            }}
          >
            {i18n.t('Common:no')}
          </Button>
        </ModalFooter>
      </Modal>
      {addItemModal()}
    </div>
  );
};
